import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setTopNavbar } from '../../../Reducers/actions/navbarActions';
import { fetchSendInvite } from '../../../Reducers/actions/userActions';
import { updateUser } from '../../../Reducers/actions/authActions';
import { getUserDisplayName } from '../../../../helpers/userDisplayName';
import Icon from '../../../components/Icon/Icon';
import { MAX_INVITES, INVITE_PATH } from '../../../constants/invitations';
import './InviteContacts.scss';

const BASE_URL = import.meta.env.VITE_SUPABASE_REDIRECT_URL;

const InviteContacts = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    
    const { user } = useSelector((state) => ({
        user: state.auth.user
    }));

    // Get contacts from location state (passed from import flow)
    const [contacts, setContacts] = useState(location.state?.contacts || []);
    const [invitingContacts, setInvitingContacts] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Get invite link
    const inviteLink = user?.userName 
        ? `${BASE_URL}${INVITE_PATH}?code=${encodeURIComponent(user.userName)}`
        : '';
    
    const shareMessage = `🎨 Discover and share amazing art! Join me on HYK and explore a world of creativity. 👉 ${inviteLink}`;

    useEffect(() => {
        dispatch(setTopNavbar({
            title: 'Invite Your Contacts',
            back: true,
        }));
    }, [dispatch]);

    // Filter contacts based on search query
    const filteredContacts = contacts.filter(contact => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const name = (contact.name || '').toLowerCase();
        const email = (contact.normalizedEmail || '').toLowerCase();
        const phone = (contact.normalizedPhone || '').toLowerCase();
        return name.includes(query) || email.includes(query) || phone.includes(query);
    });

    const handleInvite = async (contact) => {
        // Check if user has invites left
        if (user?.nbInvites >= MAX_INVITES) {
            alert(`You have reached the maximum number of invites (${MAX_INVITES}).`);
            return;
        }

        // Check if contact has email
        if (!contact.normalizedEmail) {
            alert('This contact does not have an email address. Please invite them manually.');
            return;
        }

        setInvitingContacts(prev => new Set(prev).add(contact.id || contact.normalizedEmail));

        try {
            const { success, message } = await fetchSendInvite({
                email: contact.normalizedEmail,
                sender_id: user?.user_id,
                name: getUserDisplayName(user)
            });

            if (success) {
                dispatch(updateUser({ nbInvites: user?.nbInvites + 1 }));
                // Remove the contact from the list after successful invite
                setContacts(prev => prev.filter(c => 
                    (c.id || c.normalizedEmail) !== (contact.id || contact.normalizedEmail)
                ));
            } else {
                alert(message || 'Failed to send invite. Please try again.');
            }
        } catch (error) {
            console.error('Error sending invite:', error);
            alert('Failed to send invite. Please try again.');
        } finally {
            setInvitingContacts(prev => {
                const newSet = new Set(prev);
                newSet.delete(contact.id || contact.normalizedEmail);
                return newSet;
            });
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getContactDisplayName = (contact) => {
        return contact.name || contact.normalizedEmail || contact.normalizedPhone || 'Unknown';
    };

    const getContactDisplayInfo = (contact) => {
        if (contact.normalizedPhone) {
            return contact.normalizedPhone;
        }
        if (contact.normalizedEmail) {
            return contact.normalizedEmail;
        }
        return '';
    };

    const remainingInvites = user ? MAX_INVITES - (user?.nbInvites ?? 0) : 0;

    // Sharing handlers
    const handleMessages = () => {
        if (!inviteLink) {
            alert('Please set a username before sharing your invite link.');
            return;
        }
        // Open SMS app with pre-filled message
        const smsUrl = `sms:?body=${encodeURIComponent(shareMessage)}`;
        window.location.href = smsUrl;
    };

    const handleEmail = () => {
        if (!inviteLink) {
            alert('Please set a username before sharing your invite link.');
            return;
        }
        // Open email app with pre-filled message
        const emailUrl = `mailto:?subject=${encodeURIComponent('Join me on HYK 🎨')}&body=${encodeURIComponent(shareMessage)}`;
        window.location.href = emailUrl;
    };

    const handleShareLink = async () => {
        if (!inviteLink) {
            alert('Please set a username before copying your invite link.');
            return;
        }
        try {
            await navigator.clipboard.writeText(inviteLink);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = inviteLink;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleMore = async () => {
        if (!inviteLink) {
            alert('Please set a username before sharing your invite link.');
            return;
        }
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Join me on HYK 🎨',
                    text: shareMessage,
                    url: inviteLink,
                });
            } catch (err) {
                // User cancelled or error occurred
                if (err.name !== 'AbortError') {
                    console.error('Error sharing:', err);
                }
            }
        } else {
            // Fallback: copy to clipboard if Web Share API is not available
            handleShareLink();
        }
    };

    if (contacts.length === 0) {
        return (
            <div className="invite-contacts-container">
                <div className="empty-state">
                    <Icon alias="invite" width={120} height={120} color="#FF3232" />
                    <p className="empty-title">No contacts found</p>
                    <p className="empty-subtitle">Import your contacts to invite them to HYK</p>
                    <button 
                        className="btn red-btn btn-full mt-24"
                        onClick={() => navigate('/invite-community')}
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="invite-contacts-container mt-48">
            <div className="contacts-header">
                <p className="contacts-count">
                    {filteredContacts.length} {filteredContacts.length === 1 ? 'contact' : 'contacts'}
                </p>
                {remainingInvites > 0 && (
                    <p className="invites-remaining">
                        {remainingInvites} {remainingInvites === 1 ? 'invite' : 'invites'} left
                    </p>
                )}
            </div>

            {/* Sharing options */}
            <div className="sharing-options">
                <button className="share-option-btn" onClick={handleMessages}>
                    <div className="share-icon-circle">
                        <Icon alias="mail" width={24} height={24} color="#FF3232" />
                    </div>
                    <span className="share-label">Messages</span>
                </button>
                <button className="share-option-btn" onClick={handleEmail}>
                    <div className="share-icon-circle">
                        <Icon alias="email" width={24} height={24} color="#FF3232" />
                    </div>
                    <span className="share-label">Email</span>
                </button>
                <button className="share-option-btn" onClick={handleShareLink}>
                    <div className="share-icon-circle">
                        <Icon alias="share_link" width={24} height={24} color="#FF3232" />
                    </div>
                    <span className="share-label">Share link</span>
                </button>
                <button className="share-option-btn" onClick={handleMore}>
                    <div className="share-icon-circle">
                        <Icon alias="share_android" width={24} height={24} color="#000000" />
                    </div>
                    <span className="share-label">More</span>
                </button>
            </div>
            
            {copySuccess && (
                <p className="copy-success">Link copied to clipboard!</p>
            )}

            {/* Search input */}
            <div className="search-container">
                <Icon alias="search" width={20} height={20} color="#666" className="search-icon" />
                <input
                    type="text"
                    placeholder="Search contacts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                />
            </div>

            {/* Contact list */}
            <div className="contacts-list">
                {filteredContacts.map((contact, index) => {
                    const contactId = contact.id || contact.normalizedEmail || contact.normalizedPhone || index;
                    const isInviting = invitingContacts.has(contactId);
                    const hasEmail = !!contact.normalizedEmail;
                    const canInvite = hasEmail && remainingInvites > 0 && !isInviting;

                    return (
                        <div key={contactId} className="contact-item">
                            <div className="contact-avatar">
                                {getInitials(getContactDisplayName(contact))}
                            </div>
                            <div className="contact-info">
                                <p className="contact-name">{getContactDisplayName(contact)}</p>
                                <p className="contact-details">{getContactDisplayInfo(contact)}</p>
                            </div>
                            <button
                                className={`invite-btn ${!canInvite ? 'disabled' : ''}`}
                                onClick={() => canInvite && handleInvite(contact)}
                                disabled={!canInvite || isInviting}
                            >
                                {isInviting ? (
                                    <span className="d-flex aic jcc">
                                        <span className="spinner-small mr-4"></span>
                                        Inviting...
                                    </span>
                                ) : (
                                    'INVITE'
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>

            {filteredContacts.length === 0 && searchQuery && (
                <div className="empty-search">
                    <p>No contacts found matching "{searchQuery}"</p>
                </div>
            )}
        </div>
    );
};

export default InviteContacts;

